import { DialogCloseEvent } from '@theme/dialog';
import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { Component } from '@theme/component';

/*
 * VÀLIEN — 06.09 rev.3
 *
 * Sorun: çift dokunma / pinch her zaman görselin MERKEZİNİ büyütüyordu; kullanıcı
 * kenara veya köşeye yakınlaşamadı. Sebep, ölçeklemenin her zaman elemanın
 * merkezinden (transform-origin: center) yapılmasıydı. Kaydırma (translate) ile
 * telafi edilmeye çalışılıyordu ama sınırlar merkeze göre hesaplandığı için
 * kenarlara ulaşılamıyordu.
 *
 * Çözüm: artık dokunulan nokta transform-origin olarak ayarlanıyor
 * (--drag-zoom-origin-x / --drag-zoom-origin-y). Böylece parmağınızı nereye
 * koyarsanız orası büyüyor. Kaydırma sınırları da origin'e göre asimetrik
 * hesaplanıyor, yani köşeye kadar gidebiliyorsunuz ama görsel çerçeveden kaçmıyor.
 *
 * Ayrıca (rev.2'den devam): 1x'te tek parmak hareketi engellenmiyor, böylece
 * galeri parmakla kaydırılabiliyor.
 */
const MIN_ZOOM = 1;
const MAX_ZOOM = 3.5;
const DEFAULT_ZOOM = 1;
const DOUBLE_TAP_ZOOM = 2.4;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 8;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #origin = { x: 50, y: 50 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;
  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  get #image() {
    return this.refs.image;
  }

  get #isZoomed() {
    return this.#scale > MIN_ZOOM + 0.01;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * Bir noktayı eleman içinde yüzde olarak origin'e çevirir.
   * @param {number} clientX
   * @param {number} clientY
   */
  #setOriginFromPoint(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    this.#origin = {
      x: clamp(((clientX - rect.left) / rect.width) * 100, 0, 100),
      y: clamp(((clientY - rect.top) / rect.height) * 100, 0, 100),
    };
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      preventDefault(event);

      // Pinch başlarken iki parmağın ortasını büyütme merkezi yap.
      if (!this.#isZoomed) {
        this.#setOriginFromPoint((touch1.clientX + touch2.clientX) / 2, (touch1.clientY + touch2.clientY) / 2);
        this.#translate = { x: 0, y: 0 };
      }

      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.#lastTapPosition) {
      const distance = getDistance(touch, this.#lastTapPosition);

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#handleDoubleTapFromTouch(touch);
        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);

    if (this.#isZoomed) {
      preventDefault(event);
      this.#startDragGestureFromTouch(touch);
    } else {
      this.#isDragging = false;
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2) || 1;
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Çift dokunma: 1x ise DOKUNULAN NOKTAYA yakınlaş, zaten yakınsa 1x'e dön.
   * @param {Touch} touch
   */
  #handleDoubleTapFromTouch(touch) {
    if (this.#isZoomed) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#origin = { x: 50, y: 50 };
    } else {
      this.#setOriginFromPoint(touch.clientX, touch.clientY);
      this.#translate = { x: 0, y: 0 };
      this.#scale = DOUBLE_TAP_ZOOM;
    }

    this.#isDragging = false;
    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        preventDefault(event);
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount === 1 && this.#isDragging && this.#isZoomed) {
      const touch = event.touches[0];
      if (touch) {
        preventDefault(event);
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const currentDistance = getDistance(touch1, touch2);
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Kaydırma sınırları — origin merkezde olmayabileceği için asimetrik hesaplanır.
   *
   * Ölçekli görselin soldaki taşması  = W * (s-1) * ox
   * Sağdaki taşması                    = W * (s-1) * (1-ox)
   * Görünür kayma = translate * s olduğu için sınırlar s'e bölünür.
   */
  #constrainTranslation() {
    const width = this.clientWidth;
    const height = this.clientHeight;
    if (!width || !height || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (!this.#isZoomed) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const ox = this.#origin.x / 100;
    const oy = this.#origin.y / 100;
    const overflowX = width * (this.#scale - 1);
    const overflowY = height * (this.#scale - 1);

    const maxRight = (overflowX * ox) / this.#scale;
    const maxLeft = (overflowX * (1 - ox)) / this.#scale;
    const maxDown = (overflowY * oy) / this.#scale;
    const maxUp = (overflowY * (1 - oy)) / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxLeft, maxRight);
    this.#translate.y = clamp(this.#translate.y, -maxUp, maxDown);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.style.setProperty('--drag-zoom-origin-x', `${this.#origin.x}%`);
    this.style.setProperty('--drag-zoom-origin-y', `${this.#origin.y}%`);

    this.style.touchAction = this.#isZoomed ? 'none' : 'pan-x pan-y';
  };

  #resetZoom = () => {
    this.#scale = DEFAULT_ZOOM;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#origin = { x: 50, y: 50 };

    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
    this.style.setProperty('--drag-zoom-origin-x', '50%');
    this.style.setProperty('--drag-zoom-origin-y', '50%');
    this.style.touchAction = 'pan-x pan-y';
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * @param {Point | Touch} point1
 * @param {Point | Touch} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
